import * as Sentry from '@sentry/browser'
import { useState, useEffect } from 'react'
import { flashNotification } from '../../../actions'
import { submitHomePriceIndexAnswers } from '../../../api/mikasa/requests'
import { ERRORS } from '../../../constants/messages'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectClientId } from '../../../store/selectors/client'
import { selectHomeUuid } from '../../../store/selectors/home'
import { Page } from '../../shared/Routing'
import { Analyzing } from './WizardSteps/Analyzing'
import { AppraisalDetails } from './WizardSteps/AppraisalDetails'
import { BestGuess } from './WizardSteps/BestGuess'
import { Complete } from './WizardSteps/Complete'
import { CurrentAppraisalCheck } from './WizardSteps/CurrentAppraisalCheck'
import { PropertyType } from './WizardSteps/PropertyType'
import { PurchaseDetails } from './WizardSteps/PurchaseDetails'

import styles from './HomePriceIndexWidgetWizard.module.scss'

const PROPERTY_TYPE = 'PROPERTY_TYPE'
const PURCHASE_DETAILS = 'PURCHASE_DETAILS'
const CURRENT_APPRAISAL_CHECK = 'CURRENT_APPRAISAL_CHECK'
const APPRAISAL_DETAILS = 'APPRAISAL_DETAILS'
const BEST_GUESS = 'BEST_GUESS'
const ANALYZE = 'ANALYZE'
const COMPLETE = 'COMPLETE'

const STEPS = [
  PROPERTY_TYPE,
  PURCHASE_DETAILS,
  CURRENT_APPRAISAL_CHECK,
  APPRAISAL_DETAILS,
  BEST_GUESS,
  ANALYZE,
  COMPLETE
]

type StepName = (typeof STEPS)[number]

export interface WizardValues {
  propertyType?: string
  soldPrice?: number
  soldDate?: Date
  appraisedValue?: number
  appraisedDate?: Date
  baseValue?: number // Best guess value - needed if date is before Jan. 2018
  readyToBeSubmitted?: boolean
}

export const dateIsBeforeJanuary2018 = (date: Date) => {
  const jan2018Date = new Date('2018-01-01')

  return date < jan2018Date
}

export const HomePriceIndexWidgetWizard = () => {
  const dispatch = useAppDispatch()
  const clientId = useAppSelector(selectClientId)
  const homeUuid = useAppSelector(selectHomeUuid)

  const [currentStep, setCurrentStep] = useState<StepName>(PROPERTY_TYPE)

  /**
   *  When the forms of every step are submitted, we keep track of the submitted
   *  values in this component rather than firing a POST request after every step.
   *  When the user reaches the end of the flow, we will fire the
   *  POST request from this component to submit the data collected from the entire flow
   */
  const [values, setValues] = useState<WizardValues>({})

  const handleSubmit = async () => {
    const { readyToBeSubmitted, ...formValues } = values

    try {
      await submitHomePriceIndexAnswers(clientId, homeUuid, formValues)

      setCurrentStep(ANALYZE)
    } catch (error) {
      Sentry.captureException(error)

      dispatch(
        flashNotification({
          message: [ERRORS.defaultHeadline, ERRORS.defaultMessage],
          messageType: 'error'
        })
      )
    }
  }

  useEffect(() => {
    if (values?.readyToBeSubmitted) {
      handleSubmit()
    }
  }, [values])

  return (
    <Page>
      <Page.Body className={styles.body}>
        {currentStep === PROPERTY_TYPE && (
          <PropertyType
            onComplete={incomingValues => {
              setValues({ ...values, ...incomingValues })
              setCurrentStep(PURCHASE_DETAILS)
            }}
          />
        )}
        {currentStep === PURCHASE_DETAILS && (
          <PurchaseDetails
            onComplete={incomingValues => {
              setValues({ ...values, ...incomingValues })

              setCurrentStep(CURRENT_APPRAISAL_CHECK)
            }}
          />
        )}
        {currentStep === CURRENT_APPRAISAL_CHECK && (
          <CurrentAppraisalCheck
            onComplete={incomingValues => {
              const { soldDate } = values

              if (incomingValues.hasRecentAppraisal) {
                setCurrentStep(APPRAISAL_DETAILS)
              } else if (soldDate && dateIsBeforeJanuary2018(new Date(soldDate))) {
                setCurrentStep(BEST_GUESS)
              } else {
                setValues({ ...values, readyToBeSubmitted: true })
              }
            }}
          />
        )}
        {currentStep === APPRAISAL_DETAILS && (
          <AppraisalDetails
            onComplete={incomingValues => {
              const { soldDate } = values

              if (
                dateIsBeforeJanuary2018(new Date(incomingValues.appraisedDate)) &&
                soldDate &&
                dateIsBeforeJanuary2018(new Date(soldDate))
              ) {
                setValues({ ...values, ...incomingValues })
                setCurrentStep(BEST_GUESS)
              } else {
                setValues({ ...values, ...incomingValues, readyToBeSubmitted: true })
              }
            }}
          />
        )}
        {currentStep === BEST_GUESS && (
          <BestGuess
            onComplete={incomingValues => {
              setValues({ ...values, ...incomingValues, readyToBeSubmitted: true })
            }}
          />
        )}
        {currentStep === ANALYZE && <Analyzing onComplete={() => setCurrentStep(COMPLETE)} />}
        {currentStep === COMPLETE && <Complete values={values} />}
      </Page.Body>
    </Page>
  )
}
